/**
 * Group Thread Tags — default taxonomy (single source of truth).
 *
 * This module is the ONLY place the tag vocabulary is defined. Validators
 * (`validate.ts`), the merge core (`merge.ts`), the service, and the UI all
 * import from here so the vocabulary can never drift between layers.
 *
 * Design constraints (see PLAN-group-thread-tags-20260714.md §1):
 *   • `status` and `priority` are CLOSED enums — expansion needs product
 *     re-scope, not a code change here.
 *   • `workflowLabels` is an ALLOWLIST with a hard cardinality cap. Freeform
 *     labels are rejected by the validator (mitigates taxonomy sprawl).
 *   • Governed expansion: a new workflow label may be added ONLY when it names
 *     a workflow state/route (not a topic — topics are the LLM chip label /
 *     topicSummary system), is orthogonal to existing labels, and is added to
 *     `DEFAULT_WORKFLOW_LABEL_ALLOWLIST` in the same change as its first use.
 *
 * NOTE: `status:'archived'` here is the TAG board-state and is deliberately
 * distinct from the per-user thread-index chip-lifecycle `archived` flag in
 * `src/types/chatThreadIndexFirestore.ts`. Separate documents; not overloaded.
 */

// ── status ──────────────────────────────────────────────────────────────────
export const TAG_STATUSES = [
  'open',
  'in_progress',
  'blocked',
  'waiting',
  'resolved',
  'archived',
] as const;
export type TagStatus = (typeof TAG_STATUSES)[number];
export const DEFAULT_STATUS: TagStatus = 'open';

const STATUS_SET: ReadonlySet<string> = new Set<string>(TAG_STATUSES);
export function isTagStatus(value: unknown): value is TagStatus {
  return typeof value === 'string' && STATUS_SET.has(value);
}

// ── priority (single constrained ladder, low < normal < high < urgent) ───────
export const PRIORITY_LADDER = ['low', 'normal', 'high', 'urgent'] as const;
export type TagPriority = (typeof PRIORITY_LADDER)[number];
export const DEFAULT_PRIORITY: TagPriority = 'normal';

const PRIORITY_SET: ReadonlySet<string> = new Set<string>(PRIORITY_LADDER);
export function isTagPriority(value: unknown): value is TagPriority {
  return typeof value === 'string' && PRIORITY_SET.has(value);
}

/**
 * Ordinal rank of a priority (0 = low … 3 = urgent). Used by filters to do
 * `priority >= high` range comparisons deterministically. Unknown → -1.
 */
export function priorityRank(value: TagPriority): number {
  return PRIORITY_LADDER.indexOf(value);
}

// ── workflow labels (allowlist + cap) ────────────────────────────────────────
export const DEFAULT_WORKFLOW_LABEL_ALLOWLIST = [
  'needs-review',
  'follow-up',
  'waiting-on-customer',
  'escalated',
  'bug',
  'feature-request',
  'question',
  'billing',
  'internal',
  'hot',
] as const;
export type WorkflowLabel = (typeof DEFAULT_WORKFLOW_LABEL_ALLOWLIST)[number];

/** Hard cap on labels per thread (mitigates taxonomy sprawl on a single thread). */
export const MAX_WORKFLOW_LABELS = 8;

const WORKFLOW_LABEL_SET: ReadonlySet<string> = new Set<string>(
  DEFAULT_WORKFLOW_LABEL_ALLOWLIST,
);

/**
 * A label is admissible iff it is on the allowlist AFTER normalization
 * (trim + lowercase). Callers should normalize first; this also normalizes so
 * a stray-cased input is still correctly judged.
 */
export function isAllowedWorkflowLabel(value: unknown): value is WorkflowLabel {
  if (typeof value !== 'string') return false;
  return WORKFLOW_LABEL_SET.has(value.trim().toLowerCase());
}

/** All four tag dimensions (used to key violations). */
export const TAG_FIELDS = ['status', 'ownerUserId', 'priority', 'workflowLabels'] as const;
export type TagField = (typeof TAG_FIELDS)[number];

/**
 * The three SCALAR dimensions carry a single per-field LWW stamp each.
 * `workflowLabels` is set-valued and instead uses per-label LWW registers
 * (see `merge.ts`) so concurrent adds to *different* labels both survive.
 */
export const SCALAR_TAG_FIELDS = ['status', 'ownerUserId', 'priority'] as const;
export type ScalarTagField = (typeof SCALAR_TAG_FIELDS)[number];
